// lib/supabaseQueryBuilder.ts
import { supabase } from './supabaseClient';
import { logAndThrow } from '../utils/logging';
import { buildAccountingExternalMatchesOrFilter } from './leagueScope/matchScope';

// Define the structure for pagination and filtering options
export interface QueryOptions {
    filters?: { [key: string]: any };
    pagination?: {
        page: number;
        pageSize: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
    };
}

// Define the structure for the paginated response object
export interface PaginatedResponse<T> {
    data: T[];
    count: number;
    page: number;
    pageSize: number;
    totalPages: number;
    hasMore: boolean;
}

export class SupabaseQueryBuilder {
    static buildPaginatedQuery<T extends Record<string, any>>(
        tableName: string,
        selectQuery: string,
        options: QueryOptions
    ) {
        let query = supabase
            .from(tableName)
            .select<string, T>(selectQuery, { count: 'exact' });

        // Apply filters
        if (options.filters) {
            Object.entries(options.filters).forEach(([key, value]) => {
                if (value !== undefined && value !== '') {
                    const toSnake = (k: string) => k.replace(/([A-Z])/g, '_$1').toLowerCase();
                    // Table-specific special cases
                    if (tableName === 'matches' && key === 'leagueId') {
                        const leagueIds = Array.isArray(value)
                            ? value.filter(Boolean)
                            : value !== undefined && value !== null
                                ? [value]
                                : [];
                        if (leagueIds.length === 1) {
                            query = query.eq('league_groups.league_id', leagueIds[0]);
                        } else if (leagueIds.length > 1) {
                            query = query.in('league_groups.league_id', leagueIds);
                        }
                        return;
                    }
                    if (tableName === 'teams' && key === 'leagueId') {
                        const leagueIds = Array.isArray(value)
                            ? value.filter(Boolean)
                            : value !== undefined && value !== null
                                ? [value]
                                : [];
                        if (leagueIds.length === 1) {
                            query = query.eq('league_group_teams.league_group.league_id', leagueIds[0]);
                        } else if (leagueIds.length > 1) {
                            query = query.in('league_group_teams.league_group.league_id', leagueIds);
                        }
                        return;
                    }
                    if (tableName === 'teams' && key === 'season') {
                        if (Array.isArray(value)) {
                            query = query.in('league_group_teams.league_group.season', value);
                        } else {
                            query = query.eq('league_group_teams.league_group.season', value);
                        }
                        return;
                    }
                    if (tableName === 'officials' && key === 'leagueId') {
                        // Needs `league_officials!inner(league_id, status)` in the select.
                        const leagueIds = Array.isArray(value)
                            ? value.filter(Boolean)
                            : value !== undefined && value !== null
                                ? [value]
                                : [];
                        if (leagueIds.length === 1) {
                            query = query.eq('league_officials.league_id', leagueIds[0]);
                        } else if (leagueIds.length > 1) {
                            query = query.in('league_officials.league_id', leagueIds);
                        }
                        if (leagueIds.length > 0) {
                            query = query.eq('league_officials.status', 'active');
                        }
                        return;
                    }
                    if (tableName === 'players' && key === 'leagueId') {
                        const leagueIds = Array.isArray(value)
                            ? value.filter(Boolean)
                            : value !== undefined && value !== null
                                ? [value]
                                : [];
                        if (leagueIds.length === 1) {
                            query = query.eq('player_league_memberships.league_id', leagueIds[0]);
                        } else if (leagueIds.length > 1) {
                            query = query.in('player_league_memberships.league_id', leagueIds);
                        }
                        // Always filter by active memberships when filtering by league
                        query = query.eq('player_league_memberships.is_active', true);
                        return;
                    }
                    if (tableName === 'players' && key === 'season') {
                        if (Array.isArray(value)) {
                            query = query.in('player_league_memberships.season', value);
                        } else {
                            query = query.eq('player_league_memberships.season', value);
                        }
                        // Always filter by active memberships when filtering by season
                        query = query.eq('player_league_memberships.is_active', true);
                        return;
                    }
                    if (tableName === 'match_assignments' && key === 'matchDateFrom') {
                        query = query.gte('matches.match_date', value);
                        return;
                    }
                    if (tableName === 'match_assignments' && key === 'matchDateTo') {
                        query = query.lte('matches.match_date', value);
                        return;
                    }
                    if (tableName === 'match_assignments' && key === 'matchSeason') {
                        // Embedded filter on matches.season — only effective with the
                        // matches!inner join (set when matchAccountingStatus is provided).
                        query = query.eq('matches.season', value);
                        return;
                    }
                    if (tableName === 'match_assignments' && key === 'matchAccountingStatus') {
                        if (Array.isArray(value)) {
                            query = query.in('matches.accounting_status', value);
                        } else {
                            query = query.eq('matches.accounting_status', value);
                        }
                        return;
                    }
                    if (tableName === 'match_assignments' && key === 'leagueId') {
                        const leagueIds = Array.isArray(value)
                            ? value.filter(Boolean)
                            : value !== undefined && value !== null
                                ? [value]
                                : [];
                        if (leagueIds.length === 1) {
                            query = query.eq('matches.league_groups.league_id', leagueIds[0]);
                        } else if (leagueIds.length > 1) {
                            query = query.in('matches.league_groups.league_id', leagueIds);
                        }
                        return;
                    }
                    if (tableName === 'match_assignments' && key === 'matchExternalOrganiserIds') {
                        // Accounting scope of a regional / wilaya league (lib/payments/
                        // matchAssignmentPayment.ts#withAccountingLeagueScope): `leagueId`
                        // also lists EXT_COMPETITIONS, this keeps only the external
                        // matches these leagues organise.
                        const orFilter = buildAccountingExternalMatchesOrFilter(value);
                        if (orFilter) query = query.or(orFilter, { referencedTable: 'matches' });
                        return;
                    }
                    if (key === 'search' && value) {
                        // Table-specific search columns to avoid referencing non-existent fields
                        const searchColumnsMap: Record<string, string[]> = {
                            officials: ['full_name', 'first_name', 'last_name', 'first_name_ar', 'last_name_ar', 'email', 'phone'],
                            teams: ['name', 'full_name', 'code'],
                            players: ['full_name', 'first_name', 'last_name', 'first_name_ar', 'last_name_ar', 'license_number'],
                            stadiums: ['name', 'code'],
                            leagues: ['name', 'code'],
                            league_groups: ['name', 'code', 'season'],
                        };
                        const cols = searchColumnsMap[tableName] || ['name', 'full_name'];
                        const orExpr = cols.map(c => `${c}.ilike.%${value}%`).join(',');
                        query = query.or(orExpr);
                    } else if (value === null) {
                        // Handle null values explicitly (e.g., for filtering players without teams)
                        query = query.is(toSnake(key), null);
                    } else if (key.endsWith('From')) {
                        const field = toSnake(key.replace('From', ''));
                        query = query.gte(field, value);
                    } else if (key.endsWith('To')) {
                        const field = toSnake(key.replace('To', ''));
                        query = query.lte(field, value);
                    } else if (Array.isArray(value)) {
                        query = query.in(toSnake(key), value);
                    } else {
                        query = query.eq(toSnake(key), value);
                    }
                }
            });
        }

        // Apply sorting
        if (options.pagination?.sortBy) {
            query = query.order(options.pagination.sortBy, {
                ascending: options.pagination.sortOrder === 'asc',
            });
        }

        // Apply pagination
        if (options.pagination) {
            const { page, pageSize } = options.pagination;
            const from = (page - 1) * pageSize;
            const to = from + pageSize - 1;
            query = query.range(from, to);
        }

        return query;
    }

    static async executePaginatedQuery<T extends Record<string, any>>(
        tableName: string,
        selectQuery: string,
        options: QueryOptions
    ): Promise<PaginatedResponse<T>> {
        const query = this.buildPaginatedQuery<T>(tableName, selectQuery, options);

        const { data, error, count } = await query;

        if (error) {
            return logAndThrow('executePaginatedQuery', error, { tableName, selectQuery, options });
        }

        const { page = 1, pageSize = 25 } = options.pagination || {};
        const totalCount = count || 0;
        const totalPages = Math.ceil(totalCount / pageSize);

        return {
            data: data || [], // This will now be correctly typed as T[]
            count: totalCount,
            page,
            pageSize,
            totalPages,
            hasMore: page < totalPages,
        };
    }
}